import OpenAI from 'openai';

const openai = new OpenAI({
  apiKey: import.meta.env.VITE_OPENAI_API_KEY,
  dangerouslyAllowBrowser: true,
});

/**
 * Maps OpenAI API error types to user-friendly error messages.
 */
function getErrorMessage(error) {
  if (error instanceof OpenAI?.AuthenticationError) {
    return { isInternal: true, message: 'Invalid API key or authentication failed. Please check your OpenAI API key.' };
  } else if (error instanceof OpenAI?.PermissionDeniedError) {
    return { isInternal: true, message: 'Quota exceeded or authorization failed. You may have exceeded your usage limits or do not have access to this resource.' };
  } else if (error instanceof OpenAI?.RateLimitError) {
    return { isInternal: true, message: 'Rate limit exceeded. You are sending requests too quickly. Please wait a moment and try again.' };
  } else if (error instanceof OpenAI?.InternalServerError) {
    return { isInternal: true, message: 'OpenAI service is currently unavailable. Please try again later.' };
  } else if (error instanceof OpenAI?.APIConnectionError) {
    return { isInternal: true, message: 'Unable to connect to OpenAI service. Please check your API key and internet connection.' };
  } else {
    return { isInternal: false, message: error?.message || 'An unexpected error occurred. Please try again.' };
  }
}

export const openaiService = {
  /**
   * Generates a personalized motivational message for a student based on their profile,
   * attendance patterns, belt level, and training goals.
   * 
   * @param {Object} studentContext - Student data context
   * @param {string} studentContext.name - Student's name
   * @param {string} studentContext.beltLevel - Current belt level
   * @param {number} studentContext.stripes - Number of stripes
   * @param {number} studentContext.punctualityRate - Punctuality percentage (0-100)
   * @param {number} studentContext.totalClasses - Total classes attended
   * @param {number} studentContext.punctualClasses - Number of punctual classes
   * @param {number} studentContext.lateClasses - Number of late classes
   * @param {number} studentContext.consecutivePunctualCount - Consecutive punctual classes
   * @param {number} studentContext.consecutiveLateCount - Consecutive late classes
   * @param {string} studentContext.messageType - Type of message: 'congratulation' or 'reminder'
   * @returns {Promise<Object>} Generated message with title and content
   */
  async generateMotivationalMessage(studentContext) {
    try {
      const {
        name,
        beltLevel,
        stripes,
        punctualityRate,
        totalClasses,
        punctualClasses,
        lateClasses,
        consecutivePunctualCount,
        consecutiveLateCount,
        messageType
      } = studentContext;

      const systemPrompt = `You are a motivational Brazilian Jiu-Jitsu coach who creates personalized, encouraging messages for students. Your messages should be:
- Warm, supportive, and authentic
- Specific to the student's current situation and achievements
- Culturally appropriate for martial arts training
- Written in Portuguese (Brazilian)
- Brief but impactful (2-3 sentences)
- Focus on discipline, growth, and the journey

You understand that punctuality is a core value in martial arts, reflecting discipline and respect for instructors and training partners.`;

      let userPrompt = '';

      if (messageType === 'congratulation') {
        userPrompt = `Create a congratulatory motivational message for ${name}, a ${beltLevel} belt with ${stripes} stripe(s).

Performance Context:
- Punctuality Rate: ${punctualityRate}%
- Total Classes Attended: ${totalClasses}
- Punctual Arrivals: ${punctualClasses}
- Consecutive Punctual Classes: ${consecutivePunctualCount}

This student has demonstrated excellent punctuality and discipline. Acknowledge their achievement, connect it to their martial arts journey, and encourage them to maintain this standard.

Return ONLY a JSON object with this structure:
{
  "title": "Brief encouraging title (3-5 words)",
  "message": "Personalized motivational message (2-3 sentences)"
}`;
      } else {
        userPrompt = `Create a supportive reminder message for ${name}, a ${beltLevel} belt with ${stripes} stripe(s).

Performance Context:
- Punctuality Rate: ${punctualityRate}%
- Total Classes Attended: ${totalClasses}
- Late Arrivals: ${lateClasses}
- Recent Late Classes: ${consecutiveLateCount}

This student has been arriving late recently. Gently remind them about the importance of punctuality in martial arts, offer encouragement, and motivate them to improve without being harsh or judgmental.

Return ONLY a JSON object with this structure:
{
  "title": "Brief supportive title (3-5 words)",
  "message": "Personalized reminder message (2-3 sentences)"
}`;
      }

      const response = await openai?.chat?.completions?.create({
        model: 'gpt-4o-mini',
        messages: [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: userPrompt },
        ],
        response_format: {
          type: 'json_schema',
          json_schema: {
            name: 'motivational_message',
            schema: {
              type: 'object',
              properties: {
                title: { type: 'string' },
                message: { type: 'string' }
              },
              required: ['title', 'message'],
              additionalProperties: false,
            },
          },
        },
        reasoning_effort: 'minimal',
        verbosity: 'low',
      });

      const result = JSON.parse(response?.choices?.[0]?.message?.content);
      return { data: result, error: null };
    } catch (error) {
      const errorInfo = getErrorMessage(error);
      if (errorInfo?.isInternal) {
        console.log(errorInfo?.message);
      } else {
        console.error('Error generating motivational message:', error);
      }
      return {
        data: null,
        error: { message: errorInfo?.message }
      };
    }
  },

  /**
   * Generates a general motivational message for the student portal dashboard
   * based on overall student performance and progress.
   * 
   * @param {Object} studentContext - Student data context
   * @returns {Promise<Object>} Generated motivational message
   */
  async generateDashboardMessage(studentContext) {
    try {
      if (!openai) {
        return {
          data: null,
          error: { message: 'OpenAI API key not configured. Please add your API key to enable AI features.' }
        };
      }

      const {
        name,
        beltLevel,
        stripes,
        punctualityRate,
        totalClasses,
        recentAchievements
      } = studentContext;

      const systemPrompt = `You are a motivational Brazilian Jiu-Jitsu coach creating personalized dashboard messages for students. Your messages should:
- Be encouraging and positive
- Acknowledge recent progress or achievements
- Provide specific, actionable motivation
- Written in Portuguese (Brazilian)
- Be concise (1-2 sentences)
- Reflect the martial arts philosophy of continuous improvement`;

      let userPrompt = `Create a personalized dashboard message for ${name}, a ${beltLevel} belt with ${stripes} stripe(s).

Current Status:
- Punctuality Rate: ${punctualityRate}%
- Total Classes: ${totalClasses}
- Recent Achievements: ${recentAchievements || 'Consistent training'}

Create an encouraging message that acknowledges their current progress and motivates them for their next training session.

Return ONLY a JSON object with:
{
  "message": "Personalized motivational message (1-2 sentences)"
}`;

      const response = await openai?.chat?.completions?.create({
        model: 'gpt-4o-mini',
        messages: [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: userPrompt },
        ],
        response_format: {
          type: 'json_schema',
          json_schema: {
            name: 'dashboard_message',
            schema: {
              type: 'object',
              properties: {
                message: { type: 'string' }
              },
              required: ['message'],
              additionalProperties: false,
            },
          },
        },
        reasoning_effort: 'minimal',
        verbosity: 'low',
      });

      const result = JSON.parse(response?.choices?.[0]?.message?.content);
      return { data: result, error: null };
    } catch (error) {
      const errorInfo = getErrorMessage(error);
      if (errorInfo?.isInternal) {
        console.log(errorInfo?.message);
      } else {
        console.error('Error generating dashboard message:', error);
      }
      return {
        data: null,
        error: { message: errorInfo?.message }
      };
    }
  },

  /**
   * Generates weekly milestone updates and achievement celebrations based on
   * student attendance patterns, development plan progress, and training metrics.
   * 
   * @param {Object} studentContext - Comprehensive student data
   * @param {string} studentContext.name - Student's name
   * @param {string} studentContext.beltLevel - Current belt level
   * @param {number} studentContext.stripes - Number of stripes
   * @param {number} studentContext.weeklyAttendance - Classes attended this week
   * @param {number} studentContext.weeklyPunctuality - Punctuality rate this week (0-100)
   * @param {number} studentContext.consecutivePunctualWeeks - Consecutive weeks with good punctuality
   * @param {number} studentContext.totalClassesAttended - Total classes attended
   * @param {number} studentContext.overallAttendanceRate - Overall attendance percentage
   * @param {number} studentContext.timeInCurrentBelt - Months in current belt
   * @param {number} studentContext.progressToNextBelt - Progress percentage to next belt (0-100)
   * @param {Array} studentContext.recentSkillsLearned - Skills mastered this week
   * @param {Array} studentContext.developmentPlanGoals - Current development plan goals
   * @param {Object} studentContext.weekComparison - Comparison with previous week
   * @returns {Promise<Object>} Generated milestones and achievements
   */
  async generateWeeklyMilestoneUpdate(studentContext) {
    try {
      const {
        name,
        beltLevel,
        stripes,
        weeklyAttendance,
        weeklyPunctuality,
        consecutivePunctualWeeks,
        totalClassesAttended,
        overallAttendanceRate,
        timeInCurrentBelt,
        progressToNextBelt,
        recentSkillsLearned,
        developmentPlanGoals,
        weekComparison
      } = studentContext;

      const systemPrompt = `You are an expert Brazilian Jiu-Jitsu coach specializing in student development and motivation. Your role is to:
- Analyze weekly training data and identify meaningful milestones
- Generate personalized achievement celebrations that feel authentic and earned
- Create motivational content tied to specific progress metrics
- Recognize both major achievements and incremental improvements
- Write in Portuguese (Brazilian) with warmth and authenticity
- Balance celebration with encouragement for continued growth
- Connect achievements to BJJ philosophy and values (discipline, perseverance, respect)

Your milestone updates should:
- Be specific to the student's actual progress this week
- Celebrate attendance consistency, punctuality improvements, and skill development
- Acknowledge progress toward belt advancement
- Reference development plan goals when relevant
- Feel personal and meaningful, not generic
- Include actionable encouragement for the next week`;

      let userPrompt = `Generate a weekly milestone update and achievement celebration for ${name}, a ${beltLevel} belt with ${stripes} stripe(s).

**This Week's Performance:**
- Classes Attended: ${weeklyAttendance}
- Punctuality Rate: ${weeklyPunctuality}%
- Consecutive Punctual Weeks: ${consecutivePunctualWeeks}

**Overall Progress:**
- Total Classes: ${totalClassesAttended}
- Overall Attendance Rate: ${overallAttendanceRate}%
- Time in Current Belt: ${timeInCurrentBelt} months
- Progress to Next Belt: ${progressToNextBelt}%

**Skills & Development:**
${recentSkillsLearned?.length > 0 ? `- Skills Learned This Week: ${recentSkillsLearned?.join(', ')}` : '- Consistent technique practice'}
${developmentPlanGoals?.length > 0 ? `- Current Goals: ${developmentPlanGoals?.join(', ')}` : ''}

**Week Comparison:**
${weekComparison ? `- Attendance: ${weekComparison?.attendanceChange > 0 ? '+' : ''}${weekComparison?.attendanceChange} classes vs last week
- Punctuality: ${weekComparison?.punctualityChange > 0 ? '+' : ''}${weekComparison?.punctualityChange}% vs last week` : 'First week of tracking'}

Analyze this data and generate:
1. **Milestones Achieved**: Specific achievements this week (attendance streaks, punctuality improvements, skill mastery, progress milestones)
2. **Celebration Message**: A warm, personalized celebration of their progress
3. **Next Week Goals**: Encouraging goals for continued growth

Return ONLY a JSON object with this structure:
{
  "weekSummary": "Brief overview of the week's progress (1 sentence)",
  "milestones": [
    {
      "title": "Achievement title (3-5 words)",
      "description": "Specific description of what was achieved",
      "type": "attendance|punctuality|skill|belt_progress|streak",
      "celebrationLevel": "minor|significant|major"
    }
  ],
  "celebrationMessage": {
    "title": "Celebration headline (3-5 words)",
    "message": "Personalized celebration message (2-3 sentences)"
  },
  "nextWeekGoals": [
    "Specific, achievable goal for next week"
  ],
  "motivationalQuote": "Brief BJJ-related motivational quote or principle"
}`;

      const response = await openai?.chat?.completions?.create({
        model: 'gpt-4o-mini',
        messages: [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: userPrompt },
        ],
        response_format: {
          type: 'json_schema',
          json_schema: {
            name: 'weekly_milestone_update',
            schema: {
              type: 'object',
              properties: {
                weekSummary: { type: 'string' },
                milestones: {
                  type: 'array',
                  items: {
                    type: 'object',
                    properties: {
                      title: { type: 'string' },
                      description: { type: 'string' },
                      type: { type: 'string' },
                      celebrationLevel: { type: 'string' }
                    },
                    required: ['title', 'description', 'type', 'celebrationLevel'],
                    additionalProperties: false
                  }
                },
                celebrationMessage: {
                  type: 'object',
                  properties: {
                    title: { type: 'string' },
                    message: { type: 'string' }
                  },
                  required: ['title', 'message'],
                  additionalProperties: false
                },
                nextWeekGoals: {
                  type: 'array',
                  items: { type: 'string' }
                },
                motivationalQuote: { type: 'string' }
              },
              required: ['weekSummary', 'milestones', 'celebrationMessage', 'nextWeekGoals', 'motivationalQuote'],
              additionalProperties: false,
            },
          },
        },
        reasoning_effort: 'minimal',
        verbosity: 'medium',
      });

      const result = JSON.parse(response?.choices?.[0]?.message?.content);
      return { data: result, error: null };
    } catch (error) {
      const errorInfo = getErrorMessage(error);
      if (errorInfo?.isInternal) {
        console.log(errorInfo?.message);
      } else {
        console.error('Error generating weekly milestone update:', error);
      }
      return {
        data: null,
        error: { message: errorInfo?.message }
      };
    }
  },

  /**
   * Generates personalized trial conversion sequences for academies at day 60 or day 80.
   * Analyzes unused features, usage patterns, and creates urgency-driven messaging.
   * 
   * @param {Object} conversionContext - Trial academy conversion context
   * @param {string} conversionContext.organizationName - Academy name
   * @param {string} conversionContext.ownerName - Owner's name
   * @param {number} conversionContext.sequenceDay - Day of trial (60 or 80)
   * @param {number} conversionContext.daysRemaining - Days left in trial
   * @param {number} conversionContext.totalStudents - Number of students enrolled
   * @param {number} conversionContext.totalClasses - Number of classes created
   * @param {number} conversionContext.attendanceRecords - Attendance records tracked
   * @param {Array<string>} conversionContext.unusedFeatures - List of unused feature names
   * @param {Object} conversionContext.usageSummary - Summary of platform usage
   * @param {string} conversionContext.language - Language for messages (default: 'pt-BR')
   * @returns {Promise<Object>} Generated email and SMS sequences
   */
  async generateTrialConversionSequence(conversionContext) {
    try {
      const {
        organizationName,
        ownerName,
        sequenceDay,
        daysRemaining,
        totalStudents,
        totalClasses,
        attendanceRecords,
        unusedFeatures,
        usageSummary,
        language = 'pt-BR'
      } = conversionContext;

      const systemPrompt = `You are a conversion specialist for MyDojo, a martial arts academy management platform. Your goal is to create personalized, compelling messages that:
- Highlight the value the academy has already gained from using the platform
- Identify specific unused features that could benefit their academy
- Create urgency without being pushy or aggressive
- Offer genuine support and assistance
- Use warm, professional, and encouraging tone
- Written in ${language === 'pt-BR' ? 'Portuguese (Brazilian)' : 'English'}
- Focus on the academy's success and growth potential

You understand that trial academies need gentle nudges with clear value propositions to convert to paid subscriptions.`;

      let userPrompt = '';

      if (sequenceDay === 40) {
        userPrompt = `Create a personalized conversion sequence for ${organizationName} (Owner: ${ownerName}) at DAY 40 of their 60-day trial.

Current Usage:
- Students Enrolled: ${totalStudents}
- Classes Created: ${totalClasses}
- Attendance Records: ${attendanceRecords}
- Days Remaining in Trial: ${daysRemaining}

Unused Features:
${unusedFeatures?.map(f => `- ${f}`)?.join('\n')}

Usage Summary:
${JSON.stringify(usageSummary, null, 2)}

CREATE TWO MESSAGES:

1. EMAIL: A comprehensive email (300-400 words) that:
   - Congratulates them on their progress
   - Highlights 2-3 unused features that could help their academy
   - Offers a personalized setup call or support session
   - Creates gentle urgency (20 days left)
   - Includes a clear call-to-action

2. SMS: A brief, friendly SMS (160 characters max) that:
   - Acknowledges their trial progress
   - Mentions one key unused feature
   - Offers help
   - Creates curiosity to check email

Return ONLY a JSON object with this structure:
{
  "emailSubject": "Engaging subject line (5-8 words)",
  "emailBody": "Full HTML email body with personalization",
  "smsMessage": "Brief SMS message (max 160 chars)",
  "unusedFeaturesHighlighted": ["feature1", "feature2"],
  "urgencyFactors": ["factor1", "factor2"],
  "callToAction": "Primary CTA text"
}`;
      } else {
        userPrompt = `Create a personalized conversion sequence for ${organizationName} (Owner: ${ownerName}) at DAY 50 of their 60-day trial.

Current Usage:
- Students Enrolled: ${totalStudents}
- Classes Created: ${totalClasses}
- Attendance Records: ${attendanceRecords}
- Days Remaining in Trial: ${daysRemaining} (FINAL 10 DAYS!)

Unused Features:
${unusedFeatures?.map(f => `- ${f}`)?.join('\n')}

Usage Summary:
${JSON.stringify(usageSummary, null, 2)}

CREATE TWO MESSAGES:

1. EMAIL: An urgent but supportive email (350-450 words) that:
   - Emphasizes the final 10 days of trial
   - Shows the value they've already gained - Highlights critical unused features they're missing
   - Offers immediate setup assistance
   - Creates strong urgency with clear deadline
   - Includes pricing reminder and conversion incentive
   - Clear call-to-action to subscribe

2. SMS: An urgent SMS (160 characters max) that:
   - Creates immediate urgency (10 days left!)
   - Mentions key benefit they're missing
   - Directs to email for full details
   - Includes support offer

Return ONLY a JSON object with this structure:
{
  "emailSubject": "Urgent but supportive subject line (6-10 words)",
  "emailBody": "Full HTML email body with strong personalization and urgency",
  "smsMessage": "Urgent SMS message (max 160 chars)",
  "unusedFeaturesHighlighted": ["feature1", "feature2", "feature3"],
  "urgencyFactors": ["factor1", "factor2", "factor3"],
  "callToAction": "Primary urgent CTA text"
}`;
      }

      const response = await openai?.chat?.completions?.create({
        model: 'gpt-4o-mini',
        messages: [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: userPrompt },
        ],
        response_format: {
          type: 'json_schema',
          json_schema: {
            name: 'trial_conversion_sequence',
            schema: {
              type: 'object',
              properties: {
                emailSubject: { type: 'string' },
                emailBody: { type: 'string' },
                smsMessage: { type: 'string' },
                unusedFeaturesHighlighted: {
                  type: 'array',
                  items: { type: 'string' }
                },
                urgencyFactors: {
                  type: 'array',
                  items: { type: 'string' }
                },
                callToAction: { type: 'string' }
              },
              required: ['emailSubject', 'emailBody', 'smsMessage', 'unusedFeaturesHighlighted', 'urgencyFactors', 'callToAction'],
              additionalProperties: false,
            },
          },
        },
        reasoning_effort: 'high',
        verbosity: 'high',
      });

      const result = JSON.parse(response?.choices?.[0]?.message?.content);
      return { data: result, error: null };
    } catch (error) {
      const errorInfo = getErrorMessage(error);
      if (errorInfo?.isInternal) {
        console.log(errorInfo?.message);
      } else {
        console.error('Error generating trial conversion sequence:', error);
      }
      return {
        data: null,
        error: { message: errorInfo?.message }
      };
    }
  },

  /**
   * Provides intelligent assistance for user manual queries with contextual help
   * @param {string} userQuery - User's question or request
   * @param {Object} context - Context about user and available tutorials
   * @param {Array} conversationHistory - Previous messages for context
   * @returns {Promise<string>} AI-generated helpful response
   */
  async getManualAssistance(userQuery, context, conversationHistory = []) {
    try {
      const systemPrompt = `You are an expert AI assistant for MyDojo, a Brazilian Jiu-Jitsu academy management system. Your role is to provide helpful, accurate, and actionable guidance to academy owners, coaches, and staff.

User Context:
- Role: ${context?.userRole}
- Academy: ${context?.academyName}
- Available Tutorials: ${JSON.stringify(context?.availableTutorials)}

Your responses should:
- Be clear, concise, and actionable
- Provide step-by-step instructions when appropriate
- Reference specific tutorials when relevant
- Offer troubleshooting tips for common issues
- Be encouraging and supportive
- Use Brazilian Portuguese terms when discussing BJJ concepts
- Adapt complexity based on user's role and experience

If the user asks about a feature:
1. Explain what it does and why it's useful
2. Provide step-by-step instructions
3. Mention any prerequisites or related tutorials
4. Offer tips for best practices

If the user reports a problem:
1. Acknowledge their issue
2. Ask clarifying questions if needed
3. Provide troubleshooting steps
4. Suggest when to contact support`;

      const messages = [
        { role: 'system', content: systemPrompt },
        ...conversationHistory?.slice(-10)?.map(msg => ({
          role: msg?.role,
          content: msg?.content
        })),
        { role: 'user', content: userQuery }
      ];

      const response = await openai?.chat?.completions?.create({
        model: 'gpt-4o-mini',
        messages,
        reasoning_effort: 'medium',
        verbosity: 'medium',
        max_completion_tokens: 1000
      });

      return response?.choices?.[0]?.message?.content;
    } catch (error) {
      const errorInfo = getErrorMessage(error);
      if (errorInfo?.isInternal) {
        console.log(errorInfo?.message);
      } else {
        console.error('Error in manual assistance:', error);
      }
      throw new Error(errorInfo?.message);
    }
  },

  /**
   * Performs intelligent search across tutorials using AI understanding
   * @param {string} searchQuery - User's search query
   * @param {Array} tutorialCategories - All available tutorial categories
   * @returns {Promise<Array>} Relevant tutorials ranked by relevance
   */
  async searchTutorials(searchQuery, tutorialCategories) {
    try {
      if (!openai) {
        throw new Error('OpenAI API key not configured. Please add your API key to enable AI search features.');
      }

      const allTutorials = [];
      tutorialCategories?.forEach(category => {
        category?.tutorials?.forEach(tutorial => {
          allTutorials?.push({
            ...tutorial,
            categoryId: category?.id,
            categoryTitle: category?.title,
            categoryLevel: category?.level
          });
        });
      });

      const systemPrompt = `You are a search assistant for MyDojo tutorials. Analyze the user's query and return the most relevant tutorials.

Available Tutorials:
${JSON.stringify(allTutorials, null, 2)}

Return ONLY a JSON array of tutorial IDs ranked by relevance (most relevant first). Consider:
- Semantic similarity to the query
- User intent (learning, troubleshooting, etc.)
- Prerequisites and learning path
- Difficulty level

Return format: {"tutorialIds": ["id1", "id2", "id3"]}`;

      const response = await openai?.chat?.completions?.create({
        model: 'gpt-4o-mini',
        messages: [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: searchQuery }
        ],
        response_format: {
          type: 'json_schema',
          json_schema: {
            name: 'search_results',
            schema: {
              type: 'object',
              properties: {
                tutorialIds: {
                  type: 'array',
                  items: { type: 'string' }
                }
              },
              required: ['tutorialIds'],
              additionalProperties: false
            }
          }
        },
        reasoning_effort: 'minimal',
        verbosity: 'low'
      });

      const result = JSON.parse(response?.choices?.[0]?.message?.content);
      const rankedTutorials = result?.tutorialIds
        ?.map(id => allTutorials?.find(t => t?.id === id))
        ?.filter(Boolean);

      return rankedTutorials;
    } catch (error) {
      const errorInfo = getErrorMessage(error);
      if (errorInfo?.isInternal) {
        console.log(errorInfo?.message);
      } else {
        console.error('Error in tutorial search:', error);
      }
      throw new Error(errorInfo?.message);
    }
  },

  /**
   * Diagnoses user-reported problems and provides solutions
   * @param {string} issueCategory - Category of the issue
   * @param {string} userDescription - User's description of the problem
   * @returns {Promise<Object>} Diagnosis with causes, solutions, and prevention tips
   */
  async diagnoseProblem(issueCategory, userDescription) {
    try {
      const systemPrompt = `You are a technical support expert for MyDojo. Analyze the user's issue and provide a comprehensive diagnosis.

Issue Category: ${issueCategory}

Provide:
1. A clear problem summary
2. Possible causes (2-4 items)
3. Step-by-step solutions (2-3 solutions, ordered by likelihood)
4. Prevention tips (2-3 tips)

Be specific, actionable, and empathetic. Consider common issues in academy management software.`;

      const response = await openai?.chat?.completions?.create({
        model: 'gpt-4o-mini',
        messages: [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: userDescription }
        ],
        response_format: {
          type: 'json_schema',
          json_schema: {
            name: 'diagnosis',
            schema: {
              type: 'object',
              properties: {
                problem: { type: 'string', description: 'Clear summary of the diagnosed issue' },
                possibleCauses: {
                  type: 'array',
                  items: { type: 'string' },
                  description: 'List of possible causes'
                },
                solutions: {
                  type: 'array',
                  items: { type: 'string' },
                  description: 'Step-by-step solutions'
                },
                preventionTips: {
                  type: 'array',
                  items: { type: 'string' },
                  description: 'Tips to prevent this issue in the future'
                }
              },
              required: ['problem', 'possibleCauses', 'solutions', 'preventionTips'],
              additionalProperties: false
            }
          }
        },
        reasoning_effort: 'high',
        verbosity: 'medium'
      });

      return JSON.parse(response?.choices?.[0]?.message?.content);
    } catch (error) {
      const errorInfo = getErrorMessage(error);
      if (errorInfo?.isInternal) {
        console.log(errorInfo?.message);
      } else {
        console.error('Error in problem diagnosis:', error);
      }
      throw new Error(errorInfo?.message);
    }
  },

  /**
   * Generates AI-powered chat responses for support conversations
   * 
   * @param {Array} messages - Array of message objects with role and content
   * @returns {Promise<Object>} Generated response message
   */
  async generateChatResponse(messages) {
    try {
      const response = await openai?.chat?.completions?.create({
        model: 'gpt-4o-mini',
        messages: messages,
        temperature: 0.7,
        max_tokens: 500,
      });

      const aiMessage = response?.choices?.[0]?.message?.content;
      return { data: aiMessage, error: null };
    } catch (error) {
      const errorInfo = getErrorMessage(error);
      if (errorInfo?.isInternal) {
        console.log(errorInfo?.message);
      } else {
        console.error('Error generating chat response:', error);
      }
      return {
        data: null,
        error: { message: errorInfo?.message }
      };
    }
  }
};

export default openaiService;
